import {
  Banner,
  Box,
  Button,
  FlexBox,
  Icon,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { useCollectionDelete } from 'hooks/useCollectionDelete';
import { useCollectionStats } from 'hooks/useCollectionStats';
import { ModalWrapper, useModal } from 'lib-knowledge-modal';
import { countDisplay } from 'lib-knowledge-utils/string';

import placeholderStyles from '../placeholders/placeholders.module.scss';

type CollectionDeleteModalContentProps = {
  collectionId: string;
  collectionTitle: string;
};

export function CollectionDeleteModal({
  collectionId,
  collectionTitle,
}: CollectionDeleteModalContentProps): JSX.Element {
  const { hideModal } = useModal();
  const {
    articleCount,
    categoryCount,
    queueCount,
    loading: collectionStatsLoading,
  } = useCollectionStats({
    collectionId,
  });

  const [deleteCollection, { loading: collectionDeleting }] =
    useCollectionDelete({ closeModal: hideModal });

  return (
    <ModalWrapper>
      <ModalHead title="Delete Collection" onClose={hideModal} />
      <ModalBody>
        <FlexBox flexDirection="column" gap="16px">
          <FlexBox
            padding="16px"
            alignItems="center"
            borderColor="neutral.200"
            borderRadius="100"
            gap="200"
          >
            <FlexBox alignItems="center" padding="8px">
              <Icon
                name="copy"
                color={'var(--dixa-typography-color-secondary)'}
              />
            </FlexBox>
            <FlexBox flexDirection="column">
              <Text.Heading variant="heading-3">{collectionTitle}</Text.Heading>
              {collectionStatsLoading ? (
                <StatisticsLoadingPlaceholder />
              ) : (
                <Statistics
                  categoryCount={categoryCount}
                  articleCount={articleCount}
                  queueCount={queueCount}
                />
              )}
            </FlexBox>
          </FlexBox>
          <Banner
            variant="fail"
            description={
              <Box>
                <Text.Paragraph
                  variant="2"
                  subdued
                  withMarginBottom
                  color="red.800"
                >
                  Are you sure you want to delete this collection?{' '}
                  <strong>This can not be undone.</strong>
                </Text.Paragraph>
                {(!!articleCount || !!categoryCount || !!queueCount) && (
                  <DeletionWarning
                    categoryCount={categoryCount}
                    articleCount={articleCount}
                    queueCount={queueCount}
                  />
                )}
              </Box>
            }
          />
        </FlexBox>
      </ModalBody>
      <ModalFoot>
        <FlexBox justifyContent="flex-end" gap="100">
          <Button
            disabled={collectionStatsLoading}
            variant="tertiary"
            onClick={hideModal}
          >
            Cancel
          </Button>
          <Button
            disabled={collectionStatsLoading || collectionDeleting}
            variant="danger"
            onClick={() => {
              if (collectionTitle)
                deleteCollection({ collectionId, collectionTitle });
            }}
          >
            {collectionDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </FlexBox>
      </ModalFoot>
    </ModalWrapper>
  );
}

type DeletionWarningProps = StatisticsProps;
/** Conditionally rendered warnings */
function DeletionWarning({
  articleCount,
  categoryCount,
  queueCount,
}: DeletionWarningProps) {
  return (
    <>
      <Text.Paragraph variant="2" subdued color="red.800">
        The following will also happen:
      </Text.Paragraph>
      <ul>
        {!!categoryCount && (
          <li>
            <Text.Paragraph variant="2" subdued color="red.800">
              All categories in this collection will be permanently deleted
            </Text.Paragraph>
          </li>
        )}
        {!!articleCount && (
          <li>
            <Text.Paragraph variant="2" subdued color="red.800">
              All articles will be removed from this collection (they will not
              be deleted)
            </Text.Paragraph>
          </li>
        )}
        {!!queueCount && (
          <li>
            <Text.Paragraph variant="2" subdued color="red.800">
              All queues will lose access to the articles in this collection
            </Text.Paragraph>
          </li>
        )}
      </ul>
    </>
  );
}

type StatisticsProps = {
  categoryCount: number | undefined;
  articleCount: number | undefined;
  queueCount: number | undefined;
};
function Statistics({
  articleCount,
  categoryCount,
  queueCount,
}: StatisticsProps) {
  return (
    <FlexBox gap="3px" alignItems="center">
      <Text.Paragraph variant="4" subdued>
        {countDisplay({
          count: categoryCount,
          singular: 'category',
          plural: 'categories',
        })}{' '}
        •{' '}
        {countDisplay({
          count: articleCount,
          singular: 'article',
          plural: 'articles',
        })}{' '}
        •{' '}
        {countDisplay({
          count: queueCount,
          singular: 'queue',
          plural: 'queues',
        })}
      </Text.Paragraph>
    </FlexBox>
  );
}

const StatisticsLoadingPlaceholder = () => (
  <FlexBox
    gap="3px"
    alignItems="center"
    style={{
      lineHeight: '16px',
      color: 'var(--dixa-color-neutral-200)',
    }}
  >
    <Box
      as="span"
      height="16px"
      width="70px"
      className={placeholderStyles.placeholder}
    />{' '}
    •{' '}
    <Box
      as="span"
      height="16px"
      width="60px"
      className={placeholderStyles.placeholder}
    />{' '}
    •{' '}
    <Box
      as="span"
      height="16px"
      width="50px"
      className={placeholderStyles.placeholder}
    />
  </FlexBox>
);
